import { Seo } from "@/components/Seo";
import { useState, useEffect } from "react";
import { ArrowRight, BookOpen, X, Scale, ExternalLink, FileDown, Newspaper, FileText } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

export default function KnowledgeHub() {
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [judgments, setJudgments] = useState([]);
  const [active, setActive] = useState(null);
  const [activeNews, setActiveNews] = useState(null);
  const [newsFilter, setNewsFilter] = useState("All");

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {});
    api.get("/legal").then(({ data }) => setJudgments(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = newsFilter === "All" ? "" : `?category=${encodeURIComponent(newsFilter)}`;
    api.get(`/news${params}`).then(({ data }) => setNews(data)).catch(() => {});
  }, [newsFilter]);

  const NEWS_FILTERS = ["All", "Supreme Court", "High Court", "Articles", "News Update"];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="knowledge-hub-page">
      <Seo title="Articles & Daily Digest — Judgments & Legal News | JusticeVerse" description="Stay updated with curated landmark judgments, legal news and daily digest for Indian law on JusticeVerse Articles & Daily Digest." />
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Module 06</span>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">Articles & Daily Digest</h1>

      {/* Articles */}
      <div className="flex items-end justify-between mt-14 mb-8">
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-ink">Articles &amp; Insights Publications</h2>
          <p className="text-inkmuted mt-2">Read vetted legal analyses, contract assemblies, and evidentiary dispatches.</p>
        </div>
      </div>

      <div className="space-y-5">
        {articles.map((a) => (
          <article key={a.id} className="glass-card rounded-2xl p-8" data-testid="article-card">
            {a.image_url && (
              <img src={fileUrl(a.image_url)} alt={a.title} className="w-full h-56 object-cover rounded-xl mb-6 border border-hairline" data-testid="article-image" />
            )}
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                {a.category}
                {a.pdf_url && <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 text-gold px-2 py-0.5 text-[9px]" data-testid="article-pdf-badge"><FileText className="w-3 h-3" /> PDF</span>}
              </span>
              <span className="font-mono text-[11px] text-inkfaint">{a.read_time}</span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-ink mt-4 leading-snug">{a.title}</h3>
            <p className="text-inkmuted leading-relaxed mt-3">{a.excerpt}</p>
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-hairline">
              <span className="text-sm text-inkfaint">By <span className="text-inkmuted">{a.author}</span></span>
              <button
                onClick={() => setActive(a)}
                data-testid="read-paper-btn"
                className="flex items-center gap-2 font-semibold text-sm text-ink hover:text-gold transition-colors"
              >
                Read Paper <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* News */}
      <div className="mt-20">
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-ink">Legal News &amp; Daily Digest</h2>
        <div className="flex flex-wrap gap-2 mt-6 glass-card rounded-full p-2 w-fit">
          {NEWS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setNewsFilter(f)}
              data-testid={`news-filter-${f.replace(/\s/g, "-")}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                newsFilter === f ? "bg-btn text-btnink" : "text-inkmuted hover:text-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-8">
          {news.map((n) => (
            <div key={n.id} className="glass-card rounded-2xl p-8" data-testid="news-card">
              {n.image_url && (
                <img src={fileUrl(n.image_url)} alt={n.title} className="w-full h-48 object-cover rounded-xl mb-5 border border-hairline" data-testid="news-image" />
              )}
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] bg-surface border border-hairline text-inkmuted px-3 py-1 rounded-full">{n.category}</span>
                <span className="font-mono text-[11px] text-inkfaint">• {n.date}</span>
              </div>
              <h3 className="font-serif text-2xl text-ink">{n.title}</h3>
              <p className="text-sm text-inkmuted leading-relaxed mt-3">{n.summary}</p>
              <div className="mt-5 pt-5 border-t border-hairline flex items-center justify-between gap-3">
                <span className="font-mono text-[11px] text-inkfaint">LEGAL SOURCE: <span className="text-inkmuted">{n.source}</span></span>
                {n.content && (
                  <button onClick={() => setActiveNews(n)} data-testid="read-news-btn"
                    className="flex items-center gap-2 font-semibold text-sm text-ink hover:text-gold transition-colors shrink-0">
                    Read More <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Judgments & Resources */}
      {judgments.length > 0 && (
        <div className="mt-20" data-testid="judgments-section">
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-ink">Judgments &amp; Resources</h2>
          <p className="text-inkmuted mt-2">Landmark judgments, statutes and reference material — open the link or download the PDF.</p>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            {judgments.map((d) => (
              <div key={d.id} className="glass-card rounded-2xl p-8 flex flex-col" data-testid="judgment-card">
                <div className="flex items-center gap-3 mb-3">
                  <Scale className="w-4 h-4 text-gold" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkmuted">{d.category}</span>
                  {d.date && <span className="font-mono text-[11px] text-inkfaint">• {d.date}</span>}
                </div>
                <h3 className="font-serif text-2xl text-ink">{d.title}</h3>
                {d.citation && <span className="font-mono text-[11px] gold-text mt-1">{d.citation}</span>}
                <p className="text-sm text-inkmuted leading-relaxed mt-3 flex-1 whitespace-pre-wrap">{d.summary}</p>
                <div className="flex items-center gap-2 mt-6 pt-6 border-t border-hairline">
                  {d.link_url && (
                    <a href={d.link_url} target="_blank" rel="noreferrer" data-testid="judgment-link-btn"
                      className="rounded-full bg-btn text-btnink hover:bg-btnhover px-5 py-2 text-xs font-semibold uppercase tracking-wider transition-all flex items-center gap-2">
                      <ExternalLink className="w-3.5 h-3.5" /> Open Link
                    </a>
                  )}
                  {d.attachment_url && (
                    <a href={fileUrl(d.attachment_url)} target="_blank" rel="noreferrer" data-testid="judgment-pdf-btn"
                      className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2 text-xs font-semibold uppercase tracking-wider transition-all flex items-center gap-2">
                      <FileDown className="w-3.5 h-3.5" /> Download PDF
                    </a>
                  )}
                  {d.source && !d.link_url && !d.attachment_url && (
                    <span className="font-mono text-[11px] text-inkfaint">SOURCE: <span className="text-inkmuted">{d.source}</span></span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Article reader */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-start justify-center p-4 sm:p-10 overflow-y-auto" onClick={() => setActive(null)} data-testid="article-modal">
          <div className={`glass-card rounded-3xl w-full p-8 sm:p-12 my-8 ${active.pdf_url ? "max-w-5xl" : "max-w-3xl"}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> {active.category}
              </span>
              <button onClick={() => setActive(null)} data-testid="close-article-btn" className="text-inkmuted hover:text-ink">
                <X className="w-6 h-6" />
              </button>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-5 leading-snug">{active.title}</h2>
            <p className="text-sm text-inkfaint mt-3">By {active.author} · {active.read_time}</p>
            {active.pdf_url ? (
              <div className="mt-6" data-testid="article-pdf-embed">
                <iframe
                  src={fileUrl(active.pdf_url)}
                  title={active.title}
                  className="w-full h-[80vh] rounded-xl border border-hairline bg-white"
                />
                <a
                  href={fileUrl(active.pdf_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="article-pdf-open"
                  className="inline-flex items-center gap-2 mt-4 text-sm text-gold hover:underline"
                >
                  <BookOpen className="w-4 h-4" /> Open full PDF in a new tab
                </a>
                {active.content && (
                  <div className="text-inkmuted leading-relaxed mt-8 whitespace-pre-wrap">{active.content}</div>
                )}
              </div>
            ) : (
              <>
                {active.image_url && (
                  <img src={fileUrl(active.image_url)} alt={active.title} className="w-full max-h-80 object-cover rounded-xl mt-6 border border-hairline" />
                )}
                <div className="text-inkmuted leading-relaxed mt-8 whitespace-pre-wrap">{active.content || active.excerpt}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* News reader */}
      {activeNews && (
        <div className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-start justify-center p-4 sm:p-10 overflow-y-auto" onClick={() => setActiveNews(null)} data-testid="news-modal">
          <div className="glass-card rounded-3xl max-w-3xl w-full p-8 sm:p-12 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                <Newspaper className="w-4 h-4" /> {activeNews.category}
              </span>
              <button onClick={() => setActiveNews(null)} data-testid="close-news-btn" className="text-inkmuted hover:text-ink">
                <X className="w-6 h-6" />
              </button>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-5 leading-snug">{activeNews.title}</h2>
            <p className="text-sm text-inkfaint mt-3">{activeNews.source} · {activeNews.date}</p>
            {activeNews.image_url && (
              <img src={fileUrl(activeNews.image_url)} alt={activeNews.title} className="w-full max-h-80 object-cover rounded-xl mt-6 border border-hairline" />
            )}
            <div className="text-inkmuted leading-relaxed mt-8 whitespace-pre-wrap">{activeNews.content || activeNews.summary}</div>
          </div>
        </div>
      )}
    </div>
  );
}
